import React, { useEffect, useRef, useState } from "react";
import {
  getFirestore,
  doc,
  getDoc,
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import { app } from "../firebase";
import "./InstructionPage.css";

const DEFAULT_INSTRUCTIONS = [
  {
    id: "read-carefully",
    title: "Read all questions carefully",
    text: "Make sure you understand each question before submitting your answer.",
  },
  {
    id: "complete-on-time",
    title: "Complete the test within the allotted time",
    text: "Submit your test before the timer expires.",
  },
  {
    id: "do-not-refresh",
    title: "Do not refresh the page",
    text: "Refreshing the page may interrupt your test or cause you to lose progress.",
  },
  {
    id: "do-not-switch-tabs",
    title: "Do not switch tabs or windows",
    text: "Stay on the test page while completing the assessment.",
  },
  {
    id: "fullscreen",
    title: "Stay in fullscreen mode",
    text: "Keep the test in fullscreen mode until you have submitted it.",
  },
  {
    id: "no-external-help",
    title: "Do not use external assistance",
    text: "Complete the assessment independently without using external websites or assistance.",
  },
  {
    id: "camera-required",
    title: "Camera must remain enabled",
    text: "Camera access is required while completing this test.",
  },
  {
    id: "location-required",
    title: "Location access is required",
    text: "Location permission is required before you can start the test.",
  },
];

function InstructionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const db = getFirestore(app);

  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [candidateName, setCandidateName] = useState("");
  const [candidateEmail, setCandidateEmail] = useState("");
  const [candidatePhone, setCandidatePhone] = useState("");
  const [cameraReady, setCameraReady] = useState(false);
  const [locationReady, setLocationReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [locationError, setLocationError] = useState("");
  const [starting, setStarting] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const loadForm = async () => {
      try {
        const snapshot = await getDoc(doc(db, "forms", id));

        if (!snapshot.exists()) {
          if (mounted) {
            setError("This test could not be found or may no longer be available.");
          }
          return;
        }

        const info = snapshot.data();

        if (info.activate === false) {
          if (mounted) {
            setError("This test is not currently available.");
          }
          return;
        }

        if (mounted) {
          setFormData(info);
        }
      } catch (err) {
        console.error("Error loading test:", err);
        if (mounted) {
          setError("Unable to load this test. Please try again later.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadForm();

    return () => {
      mounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [id]);

  useEffect(() => {
    if (!formData?.includeCamera) {
      setCameraReady(true);
      return undefined;
    }

    let cancelled = false;

    const requestCamera = async () => {
      try {
        setCameraError("");
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        setCameraReady(true);

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Camera permission error:", err);
        if (!cancelled) {
          setCameraReady(false);
          setCameraError(
            "Camera access is required. Please allow camera access in your browser and try again."
          );
        }
      }
    };

    requestCamera();

    return () => {
      cancelled = true;
    };
  }, [formData?.includeCamera]);

  const requestLocation = () => {
    setLocationError("");

    if (!navigator.geolocation) {
      setLocationError("Location services are not supported by this browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        sessionStorage.setItem(
          "candidateLocation",
          JSON.stringify({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
        );
        setLocationReady(true);
      },
      () => {
        setLocationReady(false);
        setLocationError(
          "Location access is required. Please allow location access and try again."
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  useEffect(() => {
    if (!formData?.includeLocation) {
      setLocationReady(true);
      return;
    }

    requestLocation();
  }, [formData?.includeLocation]);

  const getInstructions = () => {
    if (!formData) return [];

    let rawList = [];
    if (Array.isArray(formData.selectedInstructions) && formData.selectedInstructions.length > 0) {
      rawList = formData.selectedInstructions;
    } else if (Array.isArray(formData.instructions) && formData.instructions.length > 0) {
      rawList = formData.instructions;
    }

    const resolved = rawList
      .map((item, idx) => {
        if (!item) return null;

        if (typeof item === "object") {
          const matchedById = item.id ? DEFAULT_INSTRUCTIONS.find((d) => d.id === item.id) : null;
          return {
            id: item.id || matchedById?.id || `inst-${idx}`,
            title: item.title || matchedById?.title || "Instruction",
            text: item.text || matchedById?.text || "",
          };
        }

        if (typeof item === "string") {
          const matched = DEFAULT_INSTRUCTIONS.find((d) => d.id === item);
          if (matched) return matched;
          if (item.trim()) {
            return {
              id: `inst-${idx}`,
              title: "Instruction",
              text: item.trim(),
            };
          }
        }

        return null;
      })
      .filter(Boolean);

    if (resolved.length > 0) {
      return resolved;
    }

    // Fallback if no instructions were explicitly saved: show standard instructions
    const custom = String(formData?.customInstructions || formData?.customInstruction || "").trim();
    if (!custom) {
      return [
        DEFAULT_INSTRUCTIONS.find((d) => d.id === "read-carefully") || DEFAULT_INSTRUCTIONS[0],
        DEFAULT_INSTRUCTIONS.find((d) => d.id === "do-not-refresh") || DEFAULT_INSTRUCTIONS[2],
        DEFAULT_INSTRUCTIONS.find((d) => d.id === "do-not-switch-tabs") || DEFAULT_INSTRUCTIONS[3],
      ].filter(Boolean);
    }

    return [];
  };

  const instructions = getInstructions();

  const customInstruction = String(formData?.customInstructions || formData?.customInstruction || "").trim();

  const requirements = [];

  if (formData?.enableTimer) {
    requirements.push({
      icon: <AccessTimeOutlinedIcon />,
      title: "Timed test",
      text: `You have ${formData.timerDuration || "the allotted"} minute(s) to complete the test.`,
    });
  }

  if (formData?.includeCamera) {
    requirements.push({
      icon: <CameraAltOutlinedIcon />,
      title: "Camera access",
      text: cameraReady
        ? "Camera access has been granted."
        : "Camera access is required before starting.",
      ready: cameraReady,
    });
  }

  if (formData?.includeLocation) {
    requirements.push({
      icon: <LocationOnOutlinedIcon />,
      title: "Location access",
      text: locationReady
        ? "Location access has been granted."
        : "Location access is required before starting.",
      ready: locationReady,
    });
  }

  const requiresCandidateDetails = Boolean(formData?.requireName);

  const startTest = async () => {
    setError("");

    if (formData?.requireName && !candidateName.trim()) {
      setError("Please enter your name before starting the test.");
      return;
    }

    if (formData?.includeCamera && !cameraReady) {
      setError("Please allow camera access before starting the test.");
      return;
    }

    if (formData?.includeLocation && !locationReady) {
      setError("Please allow location access before starting the test.");
      return;
    }

    setStarting(true);

    sessionStorage.setItem(
      "candidateDetails",
      JSON.stringify(
        formData?.requireName
          ? {
              name: candidateName.trim(),
              email: candidateEmail.trim(),
              phone: candidatePhone.trim(),
            }
          : { name: "", email: "", phone: "" }
      )
    );

    try {
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      }
    } catch (fullscreenError) {
      console.warn("Fullscreen could not be enabled:", fullscreenError);
    }

    navigate(`/form/${id}/userform/submit`);
  };

  if (loading) {
    return (
      <div className="instruction-page instruction-loading">
        <CircularProgress />
        <Typography>Loading test...</Typography>
      </div>
    );
  }

  if (error && !formData) {
    return (
      <div className="instruction-page instruction-error-page">
        <div className="instruction-error-card">
          <div className="instruction-logo">S</div>
          <h1>Unable to open test</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="instruction-page">
      <header className="instruction-header">
        <div className="instruction-brand">
          <div className="instruction-brand-mark">S</div>
          <div>
            <strong>SUREWAY</strong>
            <span>Assessment</span>
          </div>
        </div>

        <div className="instruction-header-status">
          <span className="status-dot" />
          Test instructions
        </div>
      </header>

      <main className="instruction-container">
        <section className="instruction-hero">
          <div className="instruction-hero-icon">
            <MenuBookOutlinedIcon />
          </div>

          <div>
            <div className="instruction-eyebrow">BEFORE YOU BEGIN</div>
            <h1>{formData?.formName || "Assessment"}</h1>
            <p>
              {formData?.formDescription ||
                "Please review the instructions below before starting your test."}
            </p>
          </div>
        </section>

        {error && (
          <Alert severity="error" className="instruction-alert">
            {error}
          </Alert>
        )}

        {instructions.length > 0 || customInstruction ? (
          <section className="instruction-card">
            <div className="section-heading">
              <div>
                <h2>Test instructions</h2>
                <p>Please read the following before you start.</p>
              </div>
            </div>

            <div className="instruction-list">
              {instructions.map((instruction, index) => (
                <div className="instruction-item" key={instruction.id || index}>
                  <div className="instruction-number">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <div className="instruction-item-content">
                    <h3>{instruction.title}</h3>
                    {instruction.text && <p>{instruction.text}</p>}
                  </div>
                </div>
              ))}

              {customInstruction && (
                <div className="instruction-item">
                  <div className="instruction-number">
                    {String(instructions.length + 1).padStart(2, "0")}
                  </div>
                  <div className="instruction-item-content">
                    <h3>Additional instruction</h3>
                    <p>{customInstruction}</p>
                  </div>
                </div>
              )}
            </div>
          </section>
        ) : null}

        {requirements.length > 0 && (
          <section className="instruction-card requirements-card">
            <div className="section-heading">
              <div>
                <h2>Before you start</h2>
                <p>These requirements are based on the test settings.</p>
              </div>
            </div>

            <div className="requirements-grid">
              {requirements.map((requirement) => (
                <div className="requirement-item" key={requirement.title}>
                  <div className="requirement-icon">{requirement.icon}</div>
                  <div>
                    <h3>{requirement.title}</h3>
                    <p>{requirement.text}</p>
                  </div>
                  {requirement.ready !== undefined && (
                    <CheckCircleOutlineIcon
                      className={`requirement-check ${
                        requirement.ready ? "ready" : ""
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>

            {formData?.includeLocation && !locationReady && (
              <Button
                variant="outlined"
                onClick={requestLocation}
                className="permission-button"
                startIcon={<LocationOnOutlinedIcon />}
              >
                Allow location access
              </Button>
            )}
          </section>
        )}

        {requiresCandidateDetails && (
          <section className="instruction-card candidate-card">
            <div className="section-heading">
              <div>
                <h2>Candidate details</h2>
                <p>Enter your details before starting the test.</p>
              </div>
              <PersonOutlineRoundedIcon />
            </div>

            <div className="candidate-fields">
              {formData?.requireName && (
                <TextField
                  fullWidth
                  label="Full Name"
                  placeholder="Enter your full name"
                  value={candidateName}
                  onChange={(e) => setCandidateName(e.target.value)}
                  required={formData?.requireName}
                />
              )}

              {formData?.requireName && (
                <>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email"
                    placeholder="Enter your email"
                    value={candidateEmail}
                    onChange={(e) => setCandidateEmail(e.target.value)}
                    required
                  />

                  <TextField
                    fullWidth
                    label="Phone"
                    placeholder="Enter your phone number"
                    value={candidatePhone}
                    onChange={(e) => setCandidatePhone(e.target.value)}
                    required
                  />
                </>
              )}
            </div>
          </section>
        )}

        {cameraError && (
          <Alert severity="warning" className="instruction-alert">
            {cameraError}
          </Alert>
        )}

        {formData?.includeCamera && (
          <div className="camera-preview">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
            />
            <div>
              <CameraAltOutlinedIcon />
              Camera preview
            </div>
          </div>
        )}

        <div className="instruction-actions">
          <div className="action-note">
            <CheckCircleOutlineIcon />
            Review everything before starting.
          </div>

          <Button
            variant="contained"
            onClick={startTest}
            disabled={
              starting ||
              (formData?.includeCamera && !cameraReady) ||
              (formData?.includeLocation && !locationReady)
            }
            endIcon={
              starting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <ArrowForwardRoundedIcon />
              )
            }
            className="start-test-button"
          >
            {starting ? "Starting..." : "Get started"}
          </Button>
        </div>
      </main>
    </div>
  );
}

export default InstructionPage;
